// src/components/FeaturesSection.jsx
import React from 'react';
import { Box, Grid, Typography, List, ListItem, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import pattern from '../assets/AdobeStock_159213901.jpeg';

import featureImg1 from '../assets/feature-img-1.jpeg';
import featureImg2 from '../assets/feature-img-2.jpeg';
import featureImg3 from '../assets/feature-img-3.jpeg';
import featureImg4 from '../assets/feature-img-4.jpeg';
import featureImg5 from '../assets/feature-img-5.jpeg';

const features = [
  {
    title: 'Exclusive, Private Experience',
    description:
      'At Indy Golf Den, you get the entire space to yourself. Our single, private simulator ensures a peaceful, focused environment—no crowds, no distractions. Whether you’re practicing your swing or enjoying a round of golf with friends, this is your personal sanctuary.',
    image: featureImg1,
  },
  {
    title: 'Professional-Grade Technology',
    description: 'We use only the best equipment to bring you a truly top-tier experience. Our simulator is equipped with:',
    list: [
      'Advanced Uneekor XO Overhead Launch Monitor (ball speed, launch angle, spin rate, carry distance, smash factor)',
      'High-Performance Gaming Computer running GSPro software for smooth, lag-free gameplay',
      'Premium Projector Screen delivering sharp, vibrant visuals',
    ],
    image: featureImg2,
  },
  {
    title: 'Thousands of Courses & User-Created Adventures',
    description:
      'Play thousands of real-world and user-created golf courses—from famous championship layouts to hidden gems across the globe.',
    image: featureImg3,
  },
  {
    title: 'Perfect for Lessons and Practice',
    description:
      'The quiet, private environment is ideal for focused golf instruction. Instructors can provide clear, precise feedback while players work on their swing without distractions.',
    image: featureImg4,
  },
  {
    title: 'Comfort and Convenience',
    description: 'Our space is designed for comfort and convenience, featuring:',
    list: [
      'Private bathroom facilities',
      'Kitchen with ice maker and water',
      'On-site golf clubs available for traveling guests',
    ],
    image: featureImg5,
  },
];

const itemVariant = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.55, ease: 'easeOut' },
  }),
};

export default function FeaturesSection() {
  const theme = useTheme();

  const imgSx = {
    width: '100%',
    display: 'block',
    borderRadius: 2,
    boxShadow: 3,
    aspectRatio: '16 / 9',
    objectFit: 'cover',
  };

  return (
    <Box
      component={motion.section}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: 0.2 }}
      sx={{ position: 'relative', py: 8, px: { xs: 2, md: 6 }, overflow: 'hidden' }}
    >
      {/* subtle pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${pattern})`,
          backgroundRepeat: 'repeat',
          opacity: 0.35,
          pointerEvents: 'none',
          zIndex: -1,         // 👈 was 0
        }}
      />

      <Typography
        variant="h4"
        align="center"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          mt: { xs: 8, md: 12 }, mb: { xs: 8, md: 16 },
          fontFamily: 'lulo-one', color: '#193e2d',
          position: 'relative', zIndex: 1,
          fontSize: { xs: '1.5rem', md: '3rem' },
        }}
      >
        Why Choose Indy Golf Den?
      </Typography>

      <Grid container direction="column" spacing={{ xs: 6, md: 12 }} sx={{ position: 'relative', zIndex: 1, }}>
        {features.map((f, i) => {
          const isEven = i % 2 === 0; // 0,2,4...
          return (
            <Grid
              key={f.title}
              container
              item
              spacing={{ xs: 3, md: 6 }}
              // Mobile: column (image on top). Desktop: alternate rows with row vs row-reverse.
              direction={{ xs: 'column', md: isEven ? 'row-reverse' : 'row' }}
              alignItems="center"
            >
              {/* Image (first on mobile) */}
              <Grid item xs={12} md={6}>
                <Box component="img" src={f.image} alt={f.title} sx={imgSx} />
              </Grid>

              {/* Text */}
              <Grid item xs={12} md={6}>
                <motion.div
                  custom={i}
                  variants={itemVariant}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.25 }}
                  style={{ position: 'relative', zIndex: 1 }}   // 👈 ensure above overlay
                >
                  <Typography
                    fontFamily="lulo-one"
                    variant="h6"
                    sx={{ fontWeight: 'bold', mb: 2, color: '#193e2d' }}
                  >
                    {i + 1}. {f.title}
                  </Typography>

                  <Typography variant="body1" paragraph sx={{ color: '#193e2d' }}>
                    {f.description}
                  </Typography>

                  {f.list && (
                    <List disablePadding>
                      {f.list.map((text, idx) => (
                        <ListItem disableGutters key={idx} sx={{ pl: 2 }}>
                          <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                            <CheckCircleIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={text}
                            primaryTypographyProps={{ variant: 'body2', sx: { color: '#193e2d' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </motion.div>
              </Grid>

            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}